import { Config } from "../../config";
import { CacheService } from "./cache.service";
import axios from "axios";

class DataServiceImpl {
  static instance = null;

  static getInstance() {
    if (!DataServiceImpl.instance) {
      DataServiceImpl.instance = new DataServiceImpl();
    }
    return DataServiceImpl.instance;
  }

  prepareOptions(options) {
    options = options || {};

    let headers = options.headers || {};

    if (!headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }

    if (!headers["Authorization"]) {
      if (options.token) {
        headers["Authorization"] = `${options.token}`;
      } else {
        const token = CacheService.getFromSession("security", "token");
        if (token) {
          headers["Authorization"] = `${token}`;
        }
      }
    }

    return { headers };
  }

  prepareUrl(prefix) {
    if (prefix.startsWith("//")) {
      return window.location.protocol + prefix;
    }

    if (prefix.indexOf("http") === 0) {
      return prefix;
    }

    let url = Config.API_BASE_URL;
    if (url.substr(url.length - 1, 1) !== "/") {
      url = url + "/";
    }
    if (prefix.substr(0, 1) === "/") {
      prefix = prefix.substr(1);
    }

    if (url.startsWith("//")) {
      url = url = window.location.protocol + url;
    }

    return url + prefix;
  }

  createError(message, status, data) {
    const error = new Error(message);
    error.status = status;
    error.data = data;
    return error;
  }

  getErrorMessage(response) {
    if (!response) {
      return "Response Invalid";
    }
    const data = response.data;
    return (
      (data
        ? data.statusText || data.error || data.message || data.resultReason
        : undefined) || "Response Invalid"
    );
  }

  async get(url, options) {
    try {
      const response = await axios.get(
        this.prepareUrl(url),
        this.prepareOptions(options),
      );
      if (response && response.status === 200) {
        return response.data;
      }

      throw this.createError(
        this.getErrorMessage(response),
        response.status,
        response.data,
      );
    } catch (e) {
      if (!e.response) {
        throw e;
      }
      throw this.createError(
        this.getErrorMessage(e.response),
        e.response.status,
        e.response.data,
      );
    }
  }

  async getWithHeader(url, options) {
    try {
      const response = await axios.get(
        this.prepareUrl(url),
        this.prepareOptions(options),
      );
      if (response && response.status === 200) {
        return response;
      }

      throw this.createError(
        this.getErrorMessage(response),
        response.status,
        response.data,
      );
    } catch (e) {
      if (!e.response) {
        throw e;
      }
      throw this.createError(
        this.getErrorMessage(e.response),
        e.response.status,
        e.response.data,
      );
    }
  }

  async post(url, data, options) {
    try {
      const response = await axios.post(
        this.prepareUrl(url),
        data,
        this.prepareOptions(options),
      );
      if (response && (response.status === 200 || response.status === 201)) {
        return response.data;
      }

      throw this.createError(
        this.getErrorMessage(response),
        response.status,
        response.data,
      );
    } catch (e) {
      if (!e.response) {
        throw e;
      }
      throw this.createError(
        this.getErrorMessage(e.response),
        e.response.status,
        e.response.data,
      );
    }
  }

  postMultipleAsync(url, records, callback, options) {
    url = this.prepareUrl(url);
    options = this.prepareOptions(options);
    const executions = [];

    records.forEach((record, index) => {
      const cb = (error) => {
        const execution = {
          status: error ? "error" : "success",
          error,
          record,
          index,
        };
        executions.push(execution);
        if (options.onComplete) {
          options.onComplete(execution);
        }
      };

      axios
        .post(url, record, options)
        .then(() => {
          cb();
        })
        .catch(cb);
    });

    const checkCompleted = () => {
      if (records.length === 0 || records.length === executions.length) {
        return callback(executions);
      }

      setTimeout(() => {
        checkCompleted(executions);
      }, 1000);
    };

    checkCompleted();
  }

  async put(url, data, options) {
    try {
      const response = await axios.put(
        this.prepareUrl(url),
        data,
        this.prepareOptions(options),
      );
      if (response && response.status === 200) {
        return response.data;
      }

      throw this.createError(
        this.getErrorMessage(response),
        response.status,
        response.data,
      );
    } catch (e) {
      if (!e.response) {
        throw e;
      }
      throw this.createError(
        this.getErrorMessage(e.response),
        e.response.status,
        e.response.data,
      );
    }
  }

  async delete(url, options) {
    const response = await axios.delete(
      this.prepareUrl(url),
      this.prepareOptions(options),
    );
    if (response && (response.status === 200 || response.status === 201)) {
      return response.data;
    }
    throw this.createError("Response Invalid", response.status);
  }

  postParallel(url, records, options) {
    let requests = [];
    url = this.prepareUrl(url);
    options = this.prepareOptions(options);
    records.forEach((record) => {
      requests.push(axios.post(url, record, options));
    });

    return axios.all(requests);
  }

  getAllAsync(apis, callback, options) {
    options = this.prepareOptions(options);
    const requests = apis.map((api) =>
      axios.get(this.prepareUrl(api), options),
    );
    axios.all(requests).then((responses) => {
      callback(responses.map((response) => response.data));
    });

    return;
  }

  responseSpreader() {
    return axios.spread;
  }

  sort(data, field) {
    for (var i = 0; i < data.length; i++) {
      for (var j = i + 1; j < data.length; j++) {
        const iValue = field ? data[i][field] : data[i];
        const jValue = field ? data[j][field] : data[i];

        if (iValue && jValue && iValue > jValue) {
          const record = data[i];
          data[i] = data[j];
          data[j] = record;
        }
      }
    }
  }
}

export const DataService = DataServiceImpl.getInstance();
